import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, X, Trash2, Search } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";
import api from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { BookingFormDialog } from "../../components/admin/BookingFormDialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";

export default function AdminBookings() {
  const { t, lang } = useLanguage();
  const [bookings, setBookings] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBooking, setEditingBooking] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);

  const load = useCallback(() => {
    const params = {};
    if (statusFilter !== "all") params.status = statusFilter;
    if (search.trim()) params.search = search.trim();
    api.get("/admin/bookings", { params }).then(({ data }) => setBookings(data.reverse())).catch(() => setBookings([]));
  }, [statusFilter, search]);

  useEffect(() => {
    load();
    const interval = setInterval(load, 15000);
    return () => clearInterval(interval);
  }, [load]);

  const handleCancel = async (id) => {
    await api.patch(`/admin/bookings/${id}/cancel`);
    load();
    setConfirmAction(null);
  };
  const handleDelete = async (id) => {
    await api.delete(`/admin/bookings/${id}`);
    load();
    setConfirmAction(null);
  };

  return (
    <div data-testid="admin-bookings-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h1 className="font-serif text-2xl sm:text-3xl text-white">{t("admin.bookings.title")}</h1>
        <Button data-testid="admin-bookings-new-button" onClick={() => { setEditingBooking(null); setDialogOpen(true); }} className="bg-gold text-white hover:bg-gold-hover rounded-none">
          <Plus size={16} className="mr-1.5" /> {t("admin.bookings.newBooking")}
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        {["all", "confirmed", "cancelled"].map((s) => (
          <button key={s} data-testid={`bookings-filter-${s}`} onClick={() => setStatusFilter(s)} className={`px-4 py-1.5 text-sm rounded-md ${statusFilter === s ? "bg-gold text-white" : "bg-white/5 text-white/60"}`}>
            {t(`admin.bookings.${s}`)}
          </button>
        ))}
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <Input
            data-testid="bookings-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("admin.bookings.searchPlaceholder")}
            className="bg-[#1A1A1A] border-white/10 text-white rounded-none pl-9 h-9"
          />
        </div>
      </div>

      <div className="bg-[#141414] border border-white/10 rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[800px]">
          <thead>
            <tr className="text-left text-white/40 text-xs uppercase border-b border-white/10">
              <th className="p-4">{t("admin.bookings.appointmentNumber")}</th>
              <th className="p-4">{t("admin.bookings.customer")}</th>
              <th className="p-4">{t("admin.bookings.service")}</th>
              <th className="p-4">{t("admin.bookings.date")}</th>
              <th className="p-4">{t("admin.bookings.time")}</th>
              <th className="p-4">{t("admin.bookings.price")}</th>
              <th className="p-4">{t("common.status")}</th>
              <th className="p-4">{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {bookings.length === 0 && (
              <tr><td colSpan={8} className="p-8 text-center text-white/40" data-testid="bookings-empty-state">{t("admin.bookings.noBookings")}</td></tr>
            )}
            {bookings.map((b) => (
              <tr
                key={b.id}
                data-testid={`booking-row-${b.id}`}
                onClick={() => { setEditingBooking(b); setDialogOpen(true); }}
                className="text-white/80 cursor-pointer hover:bg-white/[0.03] transition-colors"
              >
                <td className="p-4 text-gold font-semibold" data-testid={`booking-number-${b.id}`}>#{b.appointment_number || "—"}</td>
                <td className="p-4">
                  <p>{b.customer_name}</p>
                  <p className="text-white/40 text-xs">{b.customer_email}</p>
                </td>
                <td className="p-4">{lang === "nl" ? b.service_name_nl : b.service_name_en}</td>
                <td className="p-4">{b.date}</td>
                <td className="p-4">{b.start_time}</td>
                <td className="p-4">€{b.price}</td>
                <td className="p-4">
                  <span className={`px-2 py-0.5 rounded text-xs ${b.status === "confirmed" ? "bg-green-500/10 text-green-400" : "bg-red-500/10 text-red-400"}`}>
                    {t(`admin.bookings.${b.status}`)}
                  </span>
                </td>
                <td className="p-4" onClick={(e) => e.stopPropagation()}>
                  <div className="flex gap-2">
                    <button data-testid={`booking-edit-${b.id}`} onClick={() => { setEditingBooking(b); setDialogOpen(true); }} className="text-white/50 hover:text-gold"><Pencil size={16} /></button>
                    {b.status === "confirmed" && (
                      <button data-testid={`booking-cancel-${b.id}`} onClick={() => setConfirmAction({ type: "cancel", id: b.id })} className="text-white/50 hover:text-yellow-400"><X size={16} /></button>
                    )}
                    <button data-testid={`booking-delete-${b.id}`} onClick={() => setConfirmAction({ type: "delete", id: b.id })} className="text-white/50 hover:text-red-400"><Trash2 size={16} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <BookingFormDialog open={dialogOpen} onOpenChange={setDialogOpen} booking={editingBooking} onSaved={load} />

      <AlertDialog open={!!confirmAction} onOpenChange={(v) => !v && setConfirmAction(null)}>
        <AlertDialogContent data-testid="booking-confirm-dialog" className="bg-[#141414] border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmAction?.type === "cancel" ? t("admin.bookings.cancelBooking") : t("admin.bookings.deleteBooking")}</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">
              {confirmAction?.type === "cancel" ? t("admin.bookings.confirmCancel") : t("admin.bookings.confirmDelete")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="booking-confirm-cancel" className="bg-transparent border border-white/20 text-white">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="booking-confirm-action"
              onClick={() => (confirmAction.type === "cancel" ? handleCancel(confirmAction.id) : handleDelete(confirmAction.id))}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              {t("common.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
